import { useRef } from 'react';

import PropTypes from 'prop-types';
import { generatePath, useLocation, useParams } from 'react-router-dom';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import Empty from '@zenbusiness/zui/Empty';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Text from '@zenbusiness/zui/Text';

import { isEntityUrl } from '#client/constants/routes';
import * as ROUTES from '#client/constants/routes';
import { generateZenUrl } from '#client/utils/urls';

import * as Styled from './BusinessEntitySelector.style';
import { EntityMenuItem } from './components/EntityMenuItem';
import { MenuItem } from './components/MenuItem';
import useBusinessEntitySelector from './hooks/useBusinessEntitySelector';

import useAnchorElement from '../../hooks/useAnchorElement';

const ctaText = {
  addBusiness: 'Add new business',
  formBusiness: 'Form a new business',
  viewBusinesses: 'View all companies',
};

const anchorEventProps = {
  elementName: 'entity_selector_button',
};
const baseEventProps = {
  elementName: 'entity_selector_option',
  navLevel: 1,
};

function BusinessEntitySelector({ showAddNewBusiness, showViewBusinesses }) {
  const entityMenuRef = useRef(null);
  const buttonRef = useRef(null);
  const { authenticated } = useAuthentication();
  const { pathname } = useLocation();
  const currentParams = useParams();
  const { loading, currentEntity = {}, businessEntities = [] } = useBusinessEntitySelector();
  const { loading: businessEntityLoading, businessEntityUuid } = useBusinessEntity();
  const { anchorElement, onButtonClick, onMenuClose } = useAnchorElement(entityMenuRef, buttonRef);
  const newCompanyLink = businessEntityUuid ? `/guide/company/${businessEntityUuid}?topic=start-new-llc` : '/guide/';
  const viewBusinessesLink = generatePath(ROUTES.entities);

  const newCompanyText = businessEntities.length === 0 ? ctaText.formBusiness : ctaText.addBusiness;
  const isNonEntityPage = !isEntityUrl(pathname);
  const hasMultipleEntities = businessEntities.length > 1;
  const hasMenuItems = hasMultipleEntities || showAddNewBusiness;
  const showViewBusinessesMenuItem = hasMultipleEntities && showViewBusinesses;

  const ExpandIcon = anchorElement ? ExpandLessIcon : ExpandMoreIcon;
  const menuButtonProps = {
    'aria-controls': 'entity-menu',
    'aria-haspopup': 'menu',
    clickstreamProps: anchorEventProps,
    icon: <ExpandIcon aria-hidden />,
    onClick: onButtonClick,
    open: anchorElement,
  };

  if (!authenticated || isNonEntityPage) {
    return <Empty isNonEntityPage={isNonEntityPage} authenticated={authenticated} />;
  }

  if (loading || businessEntityLoading) {
    return <Styled.Skeleton>Loading</Styled.Skeleton>;
  }

  return (
    <>
      <Styled.Button
        {...(hasMenuItems ? menuButtonProps : {})}
        aria-label={hasMenuItems ? 'entity menu' : 'current entity'}
        hasMenuItems={hasMenuItems}
        iconPosition="end"
        ref={buttonRef}
        variant="contained"
      >
        <Styled.CurrentEntityInfo>
          <Text truncate size="md">
            {currentEntity.name}
          </Text>
          {currentEntity.domesticJurisdiction && (
            <Styled.CurrentEntityJurisdiction>{currentEntity.domesticJurisdiction}</Styled.CurrentEntityJurisdiction>
          )}
        </Styled.CurrentEntityInfo>
      </Styled.Button>

      {hasMenuItems && (
        <Styled.Menu
          anchorEl={anchorElement}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          aria-expanded={Boolean(anchorElement)}
          elevation={0}
          id="entity-menu"
          keepMounted
          onClose={onMenuClose}
          open={Boolean(anchorElement)}
          ref={entityMenuRef}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {hasMultipleEntities &&
            businessEntities.map((entity, idx) => {
              // Only requires checking businessEntityUuid param in url
              const entityPath = currentParams.businessEntityUuid
                ? pathname.replace(currentParams.businessEntityUuid, entity.uuid)
                : `/dashboard/${entity.uuid}`;

              const entityItem = {
                ...entity,
                eventProps: {
                  ...baseEventProps,
                  elementText: entity.name,
                  destination: entityPath,
                  navIndex: idx + 1,
                },
                path: entityPath,
              };

              return <EntityMenuItem entity={entityItem} key={entityItem.uuid} onClick={onMenuClose} />;
            })}

          {showViewBusinessesMenuItem && (
            <MenuItem onClick={onMenuClose} variant="view-all">
              <Styled.InternalLink
                clickstreamProps={{
                  ...baseEventProps,
                  elementText: ctaText.viewBusinesses,
                  destination: generateZenUrl(viewBusinessesLink),
                }}
                role="menuitem"
                to={viewBusinessesLink}
              >
                <span>{ctaText.viewBusinesses}</span>
              </Styled.InternalLink>
            </MenuItem>
          )}

          {hasMultipleEntities && showAddNewBusiness && <Styled.Divider />}

          {showAddNewBusiness && (
            <MenuItem>
              <Styled.ExternalLink
                clickstreamProps={{
                  ...baseEventProps,
                  elementText: newCompanyText,
                  destination: generateZenUrl(newCompanyLink),
                }}
                href={newCompanyLink}
                role="menuitem"
              >
                <SharedAsset alt="" aria-hidden src="/icon/micro/line-plus-thin.svg" width={16} />
                <span>{newCompanyText}</span>
              </Styled.ExternalLink>
            </MenuItem>
          )}
        </Styled.Menu>
      )}
    </>
  );
}

BusinessEntitySelector.propTypes = {
  showAddNewBusiness: PropTypes.bool,
  showViewBusinesses: PropTypes.bool,
};

BusinessEntitySelector.defaultProps = {
  showAddNewBusiness: true,
  showViewBusinesses: true,
};

export default BusinessEntitySelector;
